/**
 * Pure, framework-agnostic store for agent-originated visual effects.
 *
 * It is the single source of truth for *transient* animation state (created /
 * updated / moved / deleted highlights) — deliberately separate from the
 * persistent domain cache (TanStack Query). It is fed validated
 * `AgentActionReceiptWireV1` receipts (see `@xavior/contracts`) and exposes a
 * per-resource-key subscription so a single changed row re-renders, not the list.
 *
 * Correctness properties (all unit-tested):
 *  - dedupe by stable `effectId` (an effect never animates twice);
 *  - causal ordering: a newer effect (by `versionAfter`, then `sequence`, then
 *    completion time) supersedes an older one; a late stale arrival is ignored;
 *  - version-gating: a concurrent newer *entity* version suppresses a stale
 *    agent highlight (`reconcileEntity`);
 *  - workspace scoping: receipts for another workspace are dropped; `reset()`
 *    clears everything on a workspace switch;
 *  - bounded memory: a capped `seen` set + per-effect expiry.
 *
 * The React layer (`app/providers/AgentEffectsContext`) wraps this with
 * `useSyncExternalStore`; the store itself has no React dependency.
 */
import type {
  AgentActionReceiptWireV1,
  AgentActionEffectWireV1,
  AgentMovementV1,
} from '@xavior/contracts';

export type AgentEffectKind =
  | 'created' | 'updated' | 'moved' | 'deleted' | 'restored' | 'submitted' | 'read';

export type AgentEffectPhase = 'active' | 'settled' | 'superseded' | 'expired';

export interface ActiveAgentEffect {
  key: string;
  effectId: string;
  executionId: string;
  kind: AgentEffectKind;
  resourceType: string;
  resourceId: string;
  instanceId?: string;
  versionBefore?: number;
  versionAfter?: number;
  changedFields?: string[];
  movement?: AgentMovementV1;
  safeLabel?: string;
  occurredAtMs: number;
  expiresAtMs: number;
  phase: AgentEffectPhase;
  /** Ordering metadata (internal, but harmless to expose). */
  sequence?: { turn: number; action: number };
  completedAtMs: number;
}

export interface AgentEffectsStore {
  /** Dispatch the effects of a validated, succeeded receipt. */
  ingest(receipt: AgentActionReceiptWireV1): void;
  /** Legacy compatibility: synthesize a "created" effect from a bare id. */
  markCreated(resourceType: string, id: string | number): void;
  getEffect(key: string): ActiveAgentEffect | null;
  list(): ActiveAgentEffect[];
  subscribe(key: string, listener: () => void): () => void;
  subscribeAll(listener: () => void): () => void;
  /** Suppress a stale effect once a newer entity version is observed. */
  reconcileEntity(key: string, currentVersion: number | undefined): void;
  /** Mark the animation for an effect finished (kept until expiry). */
  settle(key: string, effectId: string): void;
  /** Drop effects whose duration has elapsed. */
  tickExpiry(nowMs?: number): void;
  /** Clear everything (e.g. on workspace/session change). */
  reset(): void;
}

export interface AgentEffectsStoreOptions {
  /** Returns the active workspace id; receipts for another workspace are dropped. */
  workspaceId?: () => string | null;
  now?: () => number;
  /** Per-kind visible duration (ms) before expiry. */
  durations?: Partial<Record<AgentEffectKind, number>>;
  /** Max remembered effect ids (dedupe ring). */
  seenCap?: number;
}

const DEFAULT_DURATIONS: Record<AgentEffectKind, number> = {
  created: 6000,
  updated: 6000,
  moved: 6000,
  restored: 6000,
  deleted: 6000,
  submitted: 4000,
  read: 4000,
};

export function agentResourceKey(type: string, id: string | number, instanceId?: string): string {
  const base = `${type}:${id}`;
  return instanceId ? `${base}::${instanceId}` : base;
}

function effectiveKind(e: AgentActionEffectWireV1): AgentEffectKind {
  if (e.kind === 'updated' && e.presentation?.movement) return 'moved';
  return e.kind;
}

export function createAgentEffectsStore(opts: AgentEffectsStoreOptions = {}): AgentEffectsStore {
  const now = opts.now ?? (() => Date.now());
  const durations = { ...DEFAULT_DURATIONS, ...(opts.durations ?? {}) };
  const seenCap = opts.seenCap ?? 1000;

  const current = new Map<string, ActiveAgentEffect>();
  const listenersByKey = new Map<string, Set<() => void>>();
  const globalListeners = new Set<() => void>();
  const seen = new Set<string>();
  const seenOrder: string[] = [];

  function rememberSeen(effectId: string): void {
    seen.add(effectId);
    seenOrder.push(effectId);
    if (seenOrder.length > seenCap) {
      const evicted = seenOrder.shift();
      if (evicted !== undefined) seen.delete(evicted);
    }
  }

  function notify(key: string): void {
    const ls = listenersByKey.get(key);
    if (ls) for (const l of ls) l();
    for (const l of globalListeners) l();
  }

  /** > 0 ⇒ `a` is causally newer than `b`. */
  function compareCausal(a: ActiveAgentEffect, b: ActiveAgentEffect): number {
    if (a.versionAfter != null && b.versionAfter != null) return a.versionAfter - b.versionAfter;
    // A versioned (typed-receipt) effect is more authoritative than an unversioned
    // (legacy `markCreated`) one — prefer it before falling through to wall-clock,
    // otherwise a later unversioned effect overwrites a versioned one and loses its
    // presentation metadata (changedFields / movement).
    if (a.versionAfter != null) return 1
    if (b.versionAfter != null) return -1
    if (a.sequence && b.sequence) {
      return (a.sequence.turn - b.sequence.turn) || (a.sequence.action - b.sequence.action);
    }
    return a.completedAtMs - b.completedAtMs;
  }

  function dispatch(record: ActiveAgentEffect): void {
    if (seen.has(record.effectId)) return; // already animated this exact effect
    const existing = current.get(record.key);
    if (existing && compareCausal(record, existing) <= 0) return; // stale / not newer
    // Remember the id only once the effect actually WINS the slot — a rejected
    // stale arrival never animated, so it must not be permanently blacklisted
    // (it may legitimately win a later re-delivery once the winner has expired).
    rememberSeen(record.effectId);
    current.set(record.key, record);
    notify(record.key);
  }

  function recordFromEffect(
    receipt: AgentActionReceiptWireV1,
    effect: AgentActionEffectWireV1,
  ): ActiveAgentEffect {
    const occurredAtMs = now();
    const kind = effectiveKind(effect);
    const completedAtMs = Date.parse(receipt.completedAt);
    return {
      key: agentResourceKey(effect.resource.type, effect.resource.id, effect.resource.instanceId),
      effectId: effect.effectId,
      executionId: receipt.executionId,
      kind,
      resourceType: effect.resource.type,
      resourceId: effect.resource.id,
      instanceId: effect.resource.instanceId,
      versionBefore: effect.versionBefore,
      versionAfter: effect.versionAfter,
      changedFields: effect.presentation?.changedFields,
      movement: effect.presentation?.movement,
      safeLabel: effect.presentation?.safeLabel,
      occurredAtMs,
      expiresAtMs: occurredAtMs + durations[kind],
      phase: 'active',
      sequence: receipt.sequence,
      completedAtMs: Number.isFinite(completedAtMs) ? completedAtMs : occurredAtMs,
    };
  }

  let legacyCounter = 0;

  return {
    ingest(receipt) {
      if (receipt.status !== 'succeeded') return; // only committed actions animate
      const ws = opts.workspaceId ? opts.workspaceId() : null;
      if (ws != null && receipt.workspaceId !== ws) return;
      for (const effect of receipt.effects) {
        dispatch(recordFromEffect(receipt, effect));
      }
    },

    markCreated(resourceType, id) {
      const occurredAtMs = now();
      const key = agentResourceKey(resourceType, id);
      dispatch({
        key,
        effectId: `legacy:${key}:${occurredAtMs}:${legacyCounter++}`,
        executionId: 'legacy',
        kind: 'created',
        resourceType,
        resourceId: String(id),
        occurredAtMs,
        expiresAtMs: occurredAtMs + durations.created,
        phase: 'active',
        completedAtMs: occurredAtMs,
      });
    },

    getEffect(key) {
      return current.get(key) ?? null;
    },

    list() {
      return [...current.values()];
    },

    subscribe(key, listener) {
      let ls = listenersByKey.get(key);
      if (!ls) { ls = new Set(); listenersByKey.set(key, ls); }
      ls.add(listener);
      return () => {
        const cur = listenersByKey.get(key);
        if (!cur) return;
        cur.delete(listener);
        if (cur.size === 0) listenersByKey.delete(key);
      };
    },

    subscribeAll(listener) {
      globalListeners.add(listener);
      return () => { globalListeners.delete(listener); };
    },

    reconcileEntity(key, currentVersion) {
      const eff = current.get(key);
      if (!eff || currentVersion == null || eff.versionAfter == null) return;
      if (currentVersion > eff.versionAfter) {
        current.delete(key);
        notify(key);
      }
    },

    settle(key, effectId) {
      const eff = current.get(key);
      if (eff && eff.effectId === effectId && eff.phase === 'active') {
        current.set(key, { ...eff, phase: 'settled' });
        notify(key);
      }
    },

    tickExpiry(nowMs = now()) {
      for (const [key, eff] of [...current.entries()]) {
        if (eff.expiresAtMs <= nowMs) {
          current.delete(key);
          notify(key);
        }
      }
    },

    reset() {
      const keys = [...current.keys()];
      current.clear();
      seen.clear();
      seenOrder.length = 0;
      for (const key of keys) notify(key);
    },
  };
}
